// ==============================================
// MyPolaris — Ship's Papers
// ==============================================
//
// One place for every document a vessel needs. Six fixed sections (nobody
// designs a hierarchy), a Ready-to-sail view that says what is missing or
// expiring, an equipment registry, and the vessel's crew with roles. The
// vessel owns the library; the owner's plan gates it for everyone aboard.
// Single-owner boats never see roles unless they invite someone.
//
// Readability (AGENTS.md Guardrail 9): nothing under 13 px — labels and
// captions 13–14 px, primary copy 16 px+. Enforced by __tests__/readability.test.ts.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { DocumentTextIcon, PlusIcon, MagnifyingGlassIcon, SparklesIcon, CheckCircleIcon, ExclamationTriangleIcon, ChevronRightIcon, TrashIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';
import { useToastStore } from '../../store/toastStore';
import type { Section, PaperDoc, DocumentInput, Readiness, Catalog } from '../../types/papers';
import { SECTION_ORDER, ROLE_RANK } from '../../types/papers';
import { DocumentForm, DocumentPanel, typeLabel } from '../../components/papers/DocumentPanel';
import { VesselCrewPanel } from '../../components/papers/VesselCrewPanel';
import { EquipmentPanel } from '../../components/papers/EquipmentPanel';
import { SharedPapersPanel, PublishDocumentModal } from '../../components/papers/SharedPapersPanel';
import { EntryPackModal } from '../../components/papers/EntryPack';
import { CrewRequestsEntry } from '../../features/crewPapers/skipper/CrewRequestsEntry';
import { btnPrimary, btnGhost, btnDanger, input, errMsg, ExpiryPill, StatusPill, fmtDate, fmtAgo, SECTION_ICON, Modal, TakenDownBadge, FromLibraryBadge } from '../../components/papers/shared';

type Tab = 'ready' | Section | 'vesselcrew' | 'shared';

/**
 * The tab a link opens on, from ?tab= (any tab's key); anything else is no tab. The Scout's
 * Ship's Papers links here with ?tab=vesselcrew, where a crew member sets their Scout PIN.
 */
export const linkedTab = (t: string | null): Tab | null =>
  t === 'ready' || t === 'vesselcrew' || t === 'shared' || SECTION_ORDER.includes(t as Section) ? (t as Tab) : null;

const statusOf = (e: unknown) => (isAxiosError(e) ? e.response?.status : undefined);
/** 4xx answers will not change on a retry — show them at once. */
const retryUnless4xx = (n: number, e: unknown) => { const st = statusOf(e); return !(st && st >= 400 && st < 500) && n < 1; };

export function ShipsPapersPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [vid, setVid] = useState<string | null>(params.get('vessel'));
  const deepLinkLib = params.get('lib');
  const [tab, setTab] = useState<Tab>(deepLinkLib ? 'shared' : linkedTab(params.get('tab')) ?? 'ready');
  const [folder, setFolder] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [equipmentId, setEquipmentId] = useState<string | null>(null);
  const [form, setForm] = useState<{ open: boolean; preset?: Partial<DocumentInput> }>({ open: false });
  const [packOpen, setPackOpen] = useState(false);

  // Always fresh on arrival: whether a ?vessel= is still ours is decided against this list.
  const { data: access, isLoading: accessLoading, isFetching: accessFetching, error: accessError, refetch: refetchAccess } = useQuery({ queryKey: ['papers-access'], queryFn: () => api.papersAccess(), refetchOnMount: 'always' });

  /** Change tab, optionally landing on a document. Selection is set here, never cleared by an effect afterwards. */
  const goTo = (t: Tab, docId: string | null = null) => { setTab(t); setSelectedDoc(docId); setFolder(null); setQ(''); };
  /** Another vessel: nothing selected or filtered on the old one carries over. */
  const switchVessel = (v: string | null) => { setVid(v); setSelectedDoc(null); setFolder(null); setQ(''); setEquipmentId(null); };

  // No vessel yet, or one we are no longer aboard (left, removed, a stale ?vessel=): take the first we are on.
  useEffect(() => {
    if (!access || accessFetching) return;
    if (!vid || !access.vessels.some((v) => v.id === vid)) { const first = access.vessels[0]?.id ?? null; if (first !== vid) switchVessel(first); }
  }, [access, accessFetching, vid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (vid && params.get('vessel') !== vid) { params.set('vessel', vid); setParams(params, { replace: true }); } }, [vid]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: catalog } = useQuery({ queryKey: ['papers-catalog'], queryFn: () => api.papersCatalog(), staleTime: Infinity, enabled: access?.enabled === true });
  const aboard = !!vid && !!access?.vessels.some((v) => v.id === vid);
  const { data: detail, error: detailError, refetch: refetchDetail } = useQuery({ queryKey: ['papers-vessel', vid], queryFn: () => api.papersVessel(vid as string), enabled: aboard, retry: retryUnless4xx });
  // The rest wait for the vessel itself, so a refused vessel is said once, not four times.
  const ready = !!vid && !!detail;
  const { data: readiness } = useQuery({ queryKey: ['papers-readiness', vid], queryFn: () => api.papersReadiness(vid as string), enabled: ready });
  const { data: equipment } = useQuery({ queryKey: ['papers-equipment', vid], queryFn: () => api.papersEquipment(vid as string), enabled: ready });
  const section: Section | null = SECTION_ORDER.includes(tab as Section) ? (tab as Section) : null;
  const { data: docs, isLoading: docsLoading } = useQuery({
    queryKey: ['papers-docs', vid, section, folder, q, equipmentId],
    queryFn: () => api.papersDocuments(vid as string, { section: section ?? undefined, folder: folder ?? undefined, q: q || undefined, equipment_item_id: section === 'equipment' && equipmentId ? equipmentId : undefined }),
    enabled: ready && !!section,
  });
  // A vessel that refuses us (403/404) may just have dropped us: re-check the list, which moves us on.
  const refused = [403, 404].includes(statusOf(detailError) ?? 0);
  useEffect(() => { if (refused) qc.invalidateQueries({ queryKey: ['papers-access'] }); }, [refused, vid]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAll = () => {
    for (const k of ['papers-vessel', 'papers-readiness', 'papers-docs', 'papers-doc', 'papers-equipment', 'papers-pack-suggested']) qc.invalidateQueries({ queryKey: [k, vid] });
  };
  const na = useMutation({ mutationFn: ({ s, t, v }: { s: Section; t: string; v: boolean }) => api.papersChecklist(vid as string, s, t, v), onSuccess: refreshAll });

  const me = detail?.me;
  const myRole = me?.role ?? 'crew';
  const canManage = ROLE_RANK[myRole] >= ROLE_RANK.officer;
  const isCaptain = ROLE_RANK[myRole] >= ROLE_RANK.captain;
  const folders = useMemo(() => (detail?.folders ?? []).filter((f) => f.section === section), [detail, section]);
  const sectionLabel = (s: Section) => catalog?.sections.find((x) => x.key === s)?.label ?? s;
  const crewAlone = (detail?.members.filter((m) => m.status === 'active').length ?? 1) <= 1 && me?.role === 'owner';

  // ---- gate states ----
  if (accessLoading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (accessError && !access) {
    return (
      <div role="alert" className="p-6 max-w-xl mx-auto mt-10 bg-slate-800/60 border border-slate-700 rounded-2xl space-y-3 text-center">
        <p className="text-base text-slate-200">Ship's Papers could not be loaded. {errMsg(accessError, 'Check your connection and try again.')}</p>
        <button type="button" onClick={() => refetchAccess()} className={btnGhost}>Try again</button>
      </div>
    );
  }
  if (access && !access.enabled) {
    return (
      <div className="p-6 max-w-xl mx-auto mt-10">
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 text-center">
          <SparklesIcon className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Ship's Papers</h1>
          <p className="text-slate-300 mb-1">Every document your vessel needs — registration, certificates, insurance, port papers, manuals and crew certificates — in one place, with a Ready-to-sail view that tells you what's missing or about to expire.</p>
          <p className="text-slate-400 text-sm mb-6">Ship's Papers isn't included in your current plan.</p>
          <a href="/dashboard/billing" className="inline-block px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 font-bold hover:brightness-105">See plans that include it</a>
        </div>
      </div>
    );
  }

  const openAdd = (preset?: Partial<DocumentInput>) => setForm({ open: true, preset });

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* header */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2"><DocumentTextIcon className="w-6 h-6 text-amber-400" /> Ship's Papers</h1>
          <p className="text-sm text-slate-400 mt-0.5">{detail ? <>{detail.vessel.name} · you are {me?.role === 'owner' ? 'the owner' : me?.role}</> : 'Your vessel’s documents, certificates and crew papers.'}</p>
        </div>
        <span className="flex-1" />
        {access && access.vessels.length > 1 && (
          <select aria-label="Vessel" value={vid ?? ''} onChange={(e) => switchVessel(e.target.value)} className="rounded-lg bg-slate-800 border border-slate-600 text-white text-base px-3 py-2">
            {access.vessels.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
        {/* Only once the role is known: before that it defaults to crew and the form would open crew-only. */}
        {vid && catalog && detail && (myRole !== 'guest') && (
          <button type="button" onClick={() => openAdd(section && section !== 'crew' && canManage ? { section } : canManage ? undefined : { section: 'crew' })} className={btnPrimary}><PlusIcon className="w-4 h-4" /> Add document</button>
        )}
      </div>

      {/* tabs */}
      <div className="flex flex-wrap gap-1.5">
        {([['ready', '✅ Ready to sail'] as [Tab, string]]).concat(SECTION_ORDER.map((s) => [s, `${SECTION_ICON[s]} ${sectionLabel(s)}`] as [Tab, string])).concat([['vesselcrew', crewAlone ? '⚓ Vessel' : '⚓ Vessel & crew'], ['shared', '🌐 Shared libraries']]).map(([k, lbl]) => (
          <button key={k} type="button" onClick={() => goTo(k)} aria-pressed={tab === k}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${tab === k ? 'bg-amber-400 text-slate-900 border-amber-400' : 'border-slate-600 text-slate-300 hover:text-white'}`}>
            {lbl}{detail && SECTION_ORDER.includes(k as Section) && detail.sections[k] ? <span className="ml-1.5 text-[13px] opacity-80">{detail.sections[k]}</span> : null}
            {k === 'vesselcrew' && isCaptain && detail && detail.members.some((m) => m.status === 'invited') ? <span className="ml-1.5 text-[13px] opacity-80">·</span> : null}
          </button>
        ))}
      </div>

      {access && access.vessels.length === 0 ? (
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 text-base text-slate-300 max-w-2xl">
          You are not aboard any vessel with Ship's Papers. When a skipper invites you, their vessel appears here; your own papers stay with you in <a href="/papers" className="text-amber-300 hover:underline">My papers</a>.
        </div>
      ) : detailError ? (
        <div role="alert" className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 space-y-3 max-w-2xl">
          {refused ? (<>
            <p className="text-base font-semibold text-white">You can no longer see this vessel's papers.</p>
            <p className="text-sm text-slate-300">{errMsg(detailError, '')} You may have left it, been removed, or your access may have ended. Ask its owner or captain if that is a mistake.</p>
          </>) : (<>
            <p className="text-base font-semibold text-white">This vessel's papers could not be loaded.</p>
            <button type="button" onClick={() => refetchDetail()} className={btnGhost}>Try again</button>
          </>)}
          {access && access.vessels.some((v) => v.id !== vid) && (
            <div className="flex flex-wrap gap-2">{access.vessels.filter((v) => v.id !== vid).map((v) => <button key={v.id} type="button" onClick={() => switchVessel(v.id)} className={btnPrimary}>Open {v.name}</button>)}</div>
          )}
        </div>
      ) : !vid || !detail || !catalog ? <div className="text-slate-400">Loading vessel…</div> : (
        <div className={`grid grid-cols-1 ${selectedDoc ? 'lg:grid-cols-[1fr_420px]' : ''} gap-4`}>
          <div className="space-y-4 min-w-0">
            {tab === 'ready' && readiness && (
              <ReadyToSail readiness={readiness} catalogLabel={(s, t) => typeLabel(catalog, s, t)} sectionLabel={sectionLabel} isCaptain={isCaptain} canManage={canManage}
                onAdd={(s, t) => openAdd({ section: s, type: t })} onOpen={(s, id) => goTo(s, id)} onNa={(s, t, v) => na.mutate({ s, t, v })} />
            )}

            {section && section !== 'equipment' && (
              <section className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative flex-1 min-w-[220px]"><MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-2.5 text-slate-500" /><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${sectionLabel(section).toLowerCase()}…`} className={`${input} pl-10`} /></div>
                  {folders.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      <button type="button" onClick={() => setFolder(null)} aria-pressed={!folder} className={`px-3 py-1.5 rounded-full text-sm border ${!folder ? 'border-amber-400 text-amber-300' : 'border-slate-600 text-slate-300'}`}>All</button>
                      {folders.map((f) => <button key={f.folder} type="button" onClick={() => setFolder(f.folder)} aria-pressed={folder === f.folder} className={`px-3 py-1.5 rounded-full text-sm border ${folder === f.folder ? 'border-amber-400 text-amber-300' : 'border-slate-600 text-slate-300'}`}>{f.folder} <span className="opacity-70">{f.count}</span></button>)}
                    </div>
                  )}
                </div>
                {section === 'ports' && (
                  <div className="flex flex-wrap items-center gap-3 bg-slate-800/60 border border-slate-700 rounded-xl px-4 py-3">
                    <div className="flex-1 min-w-[240px]"><div className="text-base font-semibold text-white">Clearing in somewhere?</div><div className="text-sm text-slate-400">Build an entry pack — registration, insurance, last clearance, crew list — as one PDF to show or hand over.</div></div>
                    <button type="button" onClick={() => setPackOpen(true)} className={btnPrimary}>Build entry pack</button>
                  </div>
                )}
                {section === 'crew' && <p className="text-sm text-slate-400">Your own papers — passport, certificates, medical — stay with you and travel between vessels. {isCaptain ? 'As owner or captain you also see the papers of everyone aboard.' : 'Only you, the owner and the captain can see them.'}</p>}
                {section === 'crew' && isCaptain && <CrewRequestsEntry vid={vid} />}
                <DocList docs={docs} loading={docsLoading} catalog={catalog} section={section} selectedId={selectedDoc} onSelect={setSelectedDoc}
                  vid={vid} canDelete={section === 'crew' || canManage} myUserId={user?.id ?? ''} canPublish={isCaptain} onDeleted={() => { setSelectedDoc(null); refreshAll(); }}
                  empty={<EmptySection section={section} catalog={catalog} canAdd={section === 'crew' || canManage} onAdd={(t) => openAdd({ section, type: t })} />} />
              </section>
            )}

            {section === 'equipment' && (
              <div className="space-y-4">
                <EquipmentPanel vid={vid} items={equipment ?? []} myRole={myRole} selectedId={equipmentId} onSelect={setEquipmentId} onChanged={refreshAll} onAddDocument={(eid) => openAdd({ section: 'equipment', equipment_item_id: eid })} />
                <section className="space-y-2">
                  <div className="flex items-center gap-2"><h3 className="text-base font-bold text-white">{equipmentId ? `Documents for ${equipment?.find((e) => e.id === equipmentId)?.name ?? 'this item'}` : 'Equipment documents'}</h3>
                    <span className="flex-1" /><div className="relative w-64"><MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-2.5 text-slate-500" /><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className={`${input} pl-10`} /></div></div>
                  <DocList docs={docs} loading={docsLoading} catalog={catalog} section="equipment" selectedId={selectedDoc} onSelect={setSelectedDoc}
                    vid={vid} canDelete={canManage} myUserId={user?.id ?? ''} canPublish={isCaptain} onDeleted={() => { setSelectedDoc(null); refreshAll(); }}
                    empty={<div className="bg-slate-800/40 border border-dashed border-slate-700 rounded-2xl p-5 text-center text-base text-slate-300">No manuals or service records yet.{canManage && <> Use <b>+</b> on an item above, or <button type="button" onClick={() => openAdd({ section: 'equipment', equipment_item_id: equipmentId ?? undefined })} className="text-amber-300 hover:underline">add a document</button>.</>}</div>} />
                </section>
              </div>
            )}

            {tab === 'vesselcrew' && <VesselCrewPanel vid={vid} detail={detail} onChanged={refreshAll} />}
            {tab === 'shared' && access && <SharedPapersPanel catalog={catalog} vesselId={vid} vessels={access.vessels} initialLibraryId={deepLinkLib} />}
          </div>

          {selectedDoc && (
            <DocumentPanel vid={vid} docId={selectedDoc} catalog={catalog} members={detail.members} equipment={equipment ?? []} myRole={myRole} myUserId={user?.id ?? ''} onClose={() => setSelectedDoc(null)} onChanged={refreshAll} />
          )}
        </div>
      )}

      {packOpen && vid && catalog && <EntryPackModal vid={vid} vesselName={detail?.vessel.name ?? ''} catalog={catalog} canCrewList={canManage} onClose={() => setPackOpen(false)} />}

      {form.open && vid && catalog && detail && (
        <DocumentForm vid={vid} catalog={catalog} members={detail.members} equipment={equipment ?? []} initial={null} preset={form.preset} myRole={myRole}
          onClose={() => setForm({ open: false })} onSaved={(d) => { setForm({ open: false }); refreshAll(); if (d.section !== tab) goTo(d.section, d.id); else setSelectedDoc(d.id); }} />
      )}
    </div>
  );
}

// ---------------- pieces ----------------

function ReadyToSail({ readiness, catalogLabel, sectionLabel, isCaptain, canManage, onAdd, onOpen, onNa }: {
  readiness: Readiness;
  catalogLabel: (s: Section, t: string) => string; sectionLabel: (s: Section) => string; isCaptain: boolean; canManage: boolean;
  onAdd: (s: Section, t: string) => void; onOpen: (s: Section, id: string) => void; onNa: (s: Section, t: string, v: boolean) => void;
}) {
  const r = readiness;
  const banner = r.status === 'green' ? ['bg-emerald-500/15 border-emerald-500/40 text-emerald-200', 'Ready to sail — every expected document is current.', CheckCircleIcon]
    : r.status === 'amber' ? ['bg-amber-500/15 border-amber-500/40 text-amber-200', `${r.counts.expiring} document${r.counts.expiring === 1 ? '' : 's'} expiring within 90 days.`, ExclamationTriangleIcon]
    : ['bg-red-500/15 border-red-500/40 text-red-200', `${r.counts.missing ? `${r.counts.missing} missing` : ''}${r.counts.missing && r.counts.expired ? ' · ' : ''}${r.counts.expired ? `${r.counts.expired} expired` : ''} — attention needed before departure.`, ExclamationTriangleIcon];
  const Icon = banner[2] as typeof CheckCircleIcon;
  const rows = r.checklist.filter((i) => i.status !== 'na');
  const naRows = r.checklist.filter((i) => i.status === 'na');
  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border px-4 py-3 flex items-center gap-3 text-base font-semibold ${banner[0]}`}><Icon className="w-6 h-6 shrink-0" />{banner[1] as string}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[['Current', r.counts.current, 'text-emerald-300'], ['Expiring', r.counts.expiring, 'text-amber-300'], ['Expired', r.counts.expired, 'text-red-300'], ['Missing', r.counts.missing, 'text-red-300']].map(([l, n, c]) => (
          <div key={l as string} className="bg-slate-800/60 border border-slate-700 rounded-xl px-4 py-3"><div className={`text-2xl font-bold tabular-nums ${c as string}`}>{n as number}</div><div className="text-sm text-slate-400">{l as string}</div></div>
        ))}
      </div>

      <section className="bg-slate-800/60 border border-slate-700 rounded-2xl overflow-hidden">
        <h2 className="text-base font-bold text-white px-4 py-3 border-b border-slate-700">Expected documents</h2>
        <ul className="divide-y divide-slate-700/70">
          {rows.map((i) => (
            <li key={`${i.section}:${i.type}`} className="flex items-center gap-3 px-4 py-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-white">{i.label}</div>
                <div className="text-sm text-slate-400">{sectionLabel(i.section)}{i.title ? ` · ${i.title}` : ''}{i.expiresOn ? ` · ${i.status === 'expired' ? 'expired' : 'expires'} ${fmtDate(i.expiresOn)}` : ''}</div>
              </div>
              <StatusPill status={i.status} />
              {i.documentId ? <button type="button" onClick={() => onOpen(i.section, i.documentId as string)} className={btnGhost}>Open <ChevronRightIcon className="w-4 h-4" /></button>
                : canManage ? <button type="button" onClick={() => onAdd(i.section, i.type)} className={btnPrimary}><PlusIcon className="w-4 h-4" /> Add</button> : null}
              {isCaptain && !i.documentId && <button type="button" onClick={() => onNa(i.section, i.type, true)} className="text-sm text-slate-400 hover:text-white">Not applicable</button>}
            </li>
          ))}
        </ul>
        {naRows.length > 0 && isCaptain && (
          <div className="px-4 py-2 text-sm text-slate-400 border-t border-slate-700">Not applicable to this vessel: {naRows.map((i, idx) => <span key={i.type}>{idx ? ', ' : ''}{i.label} <button type="button" onClick={() => onNa(i.section, i.type, false)} className="text-amber-300 hover:underline">undo</button></span>)}</div>
        )}
      </section>

      {r.expiring.length > 0 && (
        <section className="bg-slate-800/60 border border-slate-700 rounded-2xl overflow-hidden">
          <h2 className="text-base font-bold text-white px-4 py-3 border-b border-slate-700">Expiring soon</h2>
          <ul className="divide-y divide-slate-700/70">
            {r.expiring.map((e) => (
              <li key={e.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="flex-1 min-w-0"><div className="text-base font-semibold text-white truncate">{e.title}</div><div className="text-sm text-slate-400">{catalogLabel(e.section, e.type)}{e.personal && e.ownerName ? ` · ${e.ownerName}` : ''}</div></div>
                <ExpiryPill expiresOn={e.expiresOn} />
                <button type="button" onClick={() => onOpen(e.section, e.id)} className={btnGhost}>Open</button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function DocList({ docs, loading, catalog, section, selectedId, onSelect, empty, vid, canDelete, myUserId, canPublish, onDeleted }: {
  docs: PaperDoc[] | undefined; loading: boolean; catalog: Catalog; section: Section; selectedId: string | null;
  onSelect: (id: string) => void; empty: React.ReactNode; vid: string; canDelete: boolean; myUserId: string; canPublish: boolean; onDeleted: () => void;
}) {
  // Crew & People: the owner and captain see everyone's papers but may delete only their own (the API refuses the rest).
  const deletable = (d: PaperDoc) => canDelete && (section !== 'crew' || d.ownerUserId === myUserId);
  const addToast = useToastStore((s) => s.addToast);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Selection only ever refers to rows currently listed — filters prune it.
  useEffect(() => {
    setSelected((s) => {
      if (!s.size) return s;
      const ids = new Set((docs ?? []).map((d) => d.id));
      const kept = new Set([...s].filter((id) => ids.has(id)));
      return kept.size === s.size ? s : kept;
    });
  }, [docs]);
  const del = useMutation({
    mutationFn: (ids: string[]) => api.papersBulkDeleteDocuments(vid, ids),
    onSuccess: (r) => {
      setConfirming(false);
      setSelected(new Set());
      onDeleted();
      addToast(r.failed
        ? { type: 'warning', title: `Deleted ${r.deleted} · ${r.failed} could not be deleted`, message: r.results.find((x) => !x.ok)?.error }
        : { type: 'success', title: `Deleted ${r.deleted} document${r.deleted === 1 ? '' : 's'}` });
    },
    onError: () => { setConfirming(false); addToast({ type: 'error', title: 'Could not delete', message: 'Please try again.' }); },
  });
  if (loading && !docs) return <div className="text-slate-400 text-base py-4">Loading…</div>;
  if (!docs || docs.length === 0) return <>{empty}</>;
  const pickable = docs.filter(deletable);
  const allSelected = selected.size === pickable.length && pickable.length > 0;
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div className="space-y-2">
      {pickable.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <label className="inline-flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(pickable.map((d) => d.id)))} className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-amber-400" />
            {allSelected ? `All ${pickable.length} selected` : selected.size ? `${selected.size} of ${pickable.length} selected` : `Select all ${pickable.length}${pickable.length < docs.length ? ' of yours' : ' shown'}`}
          </label>
          <span className="flex-1" />
          {selected.size > 0 && canPublish && section !== 'crew' && (
            <button type="button" onClick={() => setPublishing(true)} className={btnGhost}><GlobeAltIcon className="w-4 h-4" /> Publish {selected.size} to shared library</button>
          )}
          {selected.size > 0 && (
            <button type="button" onClick={() => setConfirming(true)} className={btnDanger}><TrashIcon className="w-4 h-4" /> Delete {selected.size}</button>
          )}
        </div>
      )}
      {publishing && section !== 'crew' && (
        <PublishDocumentModal documentIds={[...selected]} section={section} label={`${selected.size} document${selected.size === 1 ? '' : 's'}`} catalog={catalog}
          onClose={() => setPublishing(false)} />
      )}
      <ul className="bg-slate-800/60 border border-slate-700 rounded-2xl overflow-hidden divide-y divide-slate-700/70">
        {docs.map((d) => (
          <li key={d.id}>
            <div className={`w-full flex items-center gap-3 px-4 py-2.5 ${selectedId === d.id ? 'bg-amber-400/10' : selected.has(d.id) ? 'bg-red-400/5' : 'hover:bg-slate-700/40'}`}>
              {deletable(d) && (
                <input type="checkbox" aria-label={`Select ${d.title}`} checked={selected.has(d.id)} onChange={() => toggle(d.id)}
                  className="w-5 h-5 shrink-0 rounded bg-slate-700 border-slate-600 text-amber-400" />
              )}
              <button type="button" onClick={() => onSelect(d.id)} aria-pressed={selectedId === d.id} className="flex-1 min-w-0 text-left flex items-center gap-3">
                <DocumentTextIcon className={`w-6 h-6 shrink-0 ${d.fileCount ? 'text-amber-400' : 'text-slate-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-white truncate">{d.title}</div>
                  <div className="text-sm text-slate-400 truncate">{typeLabel(catalog, section, d.type)}{d.folder ? ` · ${d.folder}` : ''}{d.issuer ? ` · ${d.issuer}` : ''}{section === 'crew' && d.ownerName ? ` · ${d.ownerName}` : ''}{!d.fileCount && !d.url ? ' · no file yet' : ''}{d.url && !d.fileCount ? ' · link' : ''}</div>
                </div>
                {d.sourceLibraryTakenDown ? <TakenDownBadge /> : d.origin === 'library' && <FromLibraryBadge />}
                <ExpiryPill expiresOn={d.expiresOn} />
                <span className="text-[13px] text-slate-500 whitespace-nowrap hidden sm:inline">{fmtAgo(d.updatedAt)}</span>
              </button>
            </div>
          </li>
        ))}
      </ul>
      {confirming && (
        <Modal title={`Delete ${selected.size} document${selected.size === 1 ? '' : 's'}?`} onClose={() => !del.isPending && setConfirming(false)}>
          <p className="text-base text-slate-300">
            Files are removed from this vessel{selected.size === 1 ? '' : ' — all of them'}. Documents published to a shared library disappear from it; copies other people already took are theirs. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setConfirming(false)} disabled={del.isPending} className={btnGhost}>Cancel</button>
            <button type="button" onClick={() => del.mutate([...selected])} disabled={del.isPending} className={`${btnPrimary} !bg-red-500 !text-white`}>
              {del.isPending ? 'Deleting…' : `Delete ${selected.size}`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function EmptySection({ section, catalog, canAdd, onAdd }: { section: Section; catalog: Catalog; canAdd: boolean; onAdd: (type: string) => void }) {
  const types = catalog.sections.find((s) => s.key === section)?.types.filter((t) => t.key !== 'other') ?? [];
  const blurb: Record<Section, string> = {
    vessel: 'The boat’s identity — registration, radio licence, EPIRB, survey.', certificates: 'Almost all of these expire; file them once and Ready to sail keeps watch.',
    ports: 'Clearances, permits and forms — one folder per country keeps each visit together.', equipment: '', crew: 'Your own papers, kept with you.', other: 'Berth contracts, receipts, anything that doesn’t fit elsewhere.',
  };
  return (
    <div className="bg-slate-800/40 border border-dashed border-slate-700 rounded-2xl p-5">
      <p className="text-base text-slate-300 mb-3">{blurb[section]}{canAdd ? ' Start with one of these:' : ''}</p>
      {canAdd && <div className="flex flex-wrap gap-2">{types.slice(0, 8).map((t) => <button key={t.key} type="button" onClick={() => onAdd(t.key)} className={btnGhost}><PlusIcon className="w-4 h-4" /> {t.label}</button>)}</div>}
    </div>
  );
}

